// src/components/dashboards/composition/AppliesTo.tsx
//
// **Applies to**: which blocks on the page a source steers - a click on a
// chart, or an open control (plan 2026-10-06 dashboard interaction settings,
// S4; extracted from verity-1a's ControlSettingsPanel). It is the body of the
// one Interactions section (`InteractionsSettings`, plan 2026-10-10 settings
// catalogue B4), so a block and a control cannot drift apart.
//
// It holds no transport: "Whole page" asks the host to store `auto`
// (`onWholePage`), and the host writes it through its own path. "Selected"
// opens the Edit interactions mode with this item as the source, where the
// author switches tiles on and off on the dashboard itself.
import { useKindCopy } from "@/context/DashboardKindContext";
import { Radio, Stack, Text } from "@mantine/core";
import { ApiError } from "@/api/client";
import type { DashboardItemDTO } from "@/api/dashboards";
import type { ControlPageContext } from "@/context/DashboardTabFilterContext";
import { useDashboardInteractionEdit } from "@/context/DashboardInteractionEditContext";
import { interactionTargetsOf } from "@/utils/tabFilters";
import { blockedByOwnPeriod, canTake } from "@/components/dashboards/interactions/interactionEdit";
import { PERIOD_DIMENSION } from "@/utils/dateControlPeriods";
import { SETTINGS_COPY } from "./settingsCopy";

const COPY = SETTINGS_COPY.appliesTo;

export function errorText(err: unknown): string {
  if (err instanceof ApiError && typeof err.detail === "string" && err.detail) return err.detail;
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong. Try again.";
}

/** How many blocks on the page the source steers now, and how many could take
 *  one of its dimensions: the `tileFilters` rule (scope intersected with
 *  reach). */
export function appliesToCount(
  item: Pick<DashboardItemDTO, "id" | "chart_spec_json">,
  dimensions: readonly string[],
  pageItems: ControlPageContext["items"],
): { reached: number; carriers: number } {
  const dims = dimensions.map((key) => ({ key, label: key }));
  const carriers = pageItems.filter((i) => i.id !== item.id && canTake(i, dims));
  const targets = interactionTargetsOf(item.chart_spec_json);
  const reached =
    targets === "auto"
      ? carriers.length
      : carriers.filter((c) => c.block_key !== undefined && targets.includes(c.block_key)).length;
  return { reached, carriers: carriers.length };
}

/** How many data tiles on the page a date range control cannot reach because
 *  they carry a period of their own (ADR-0445 decision 5). */
export function ownPeriodCount(
  item: Pick<DashboardItemDTO, "id">,
  dimensions: readonly string[],
  pageItems: ControlPageContext["items"],
): number {
  const dims = dimensions.map((key) => ({ key, label: key }));
  return pageItems.filter((i) => i.id !== item.id && blockedByOwnPeriod(i, dims)).length;
}

export type AppliesToProps = {
  item: DashboardItemDTO;
  /** The dimensions the source filters on. Empty: nothing to apply yet, the
   *  radios are disabled and `emptyHint` says why. */
  dimensions: readonly string[];
  page: Pick<ControlPageContext, "items"> | null;
  /** Store `auto` (Whole page). The host owns the write and its error. */
  onWholePage: () => void;
  /** A write of the host is in flight: the radios wait for it. */
  writing?: boolean;
  /** What the source steers, in the copy: "chart" for a control (it filters
   *  charts), "block" for a clicked block. */
  noun: "chart" | "block";
  /** Shown while `dimensions` is empty. */
  emptyHint?: string;
  /** Off from outside (a block whose clicks filter nothing). */
  disabled?: boolean;
  /** Prefix of the test ids, so each panel keeps its own. */
  testIdPrefix: string;
};

export default function AppliesTo({
  item,
  dimensions,
  page,
  onWholePage,
  writing = false,
  noun,
  emptyHint,
  disabled = false,
  testIdPrefix,
}: AppliesToProps) {
  const { say } = useKindCopy();
  const interactionEdit = useDashboardInteractionEdit();
  const targets = interactionTargetsOf(item.chart_spec_json);
  const editingThis = interactionEdit?.editing?.source.id === item.id;
  // A source that filters nothing (`[]`, its switch off) is not "Selected with
  // nothing selected": the radios are disabled and read Whole page.
  const filtersNothing = Array.isArray(targets) && targets.length === 0 && disabled;
  const value = (targets !== "auto" && !filtersNothing) || editingThis ? "selected" : "page";
  const ready = dimensions.length > 0;
  const count = ready && page && !disabled ? appliesToCount(item, dimensions, page.items) : null;
  const fixed = count && page ? ownPeriodCount(item, dimensions, page.items) : 0;
  const plural = `${noun}s`;

  const choose = (next: string) => {
    if (next === "selected") {
      interactionEdit?.start(item.id);
      return;
    }
    if (editingThis) interactionEdit?.stop();
    if (targets === "auto") return;
    onWholePage();
  };

  const off = !ready || writing || disabled;
  return (
    <Stack gap={4} data-testid={`${testIdPrefix}-applies-to`}>
      <Radio.Group label={COPY.label} value={value}
        onChange={choose}
      >
        <Stack gap={6} mt={4}>
          <Radio value="page" label={SETTINGS_COPY.common.wholePage} disabled={off} />
          <Radio
            value="selected"
            label={COPY.selected(plural)}
            disabled={off || !interactionEdit}
            // Already on Selected (a list, or a control placed on no chart):
            // a click picks the charts again; the group's onChange does not
            // fire for the value it already holds.
            onClick={() => {
              if (value === "selected" && !editingThis) interactionEdit?.start(item.id);
            }}
          />
        </Stack>
      </Radio.Group>
      {!ready ? (
        emptyHint ? (
          <Text size="xs" c="dimmed">
            {emptyHint}
          </Text>
        ) : null
      ) : count ? (
        <Text size="xs" c="dimmed" data-testid={`${testIdPrefix}-applies-count`}>
          {COPY.count(count.reached, noun, targets !== "auto" ? count.carriers : null, dimensions.includes(PERIOD_DIMENSION.key))}
        </Text>
      ) : null}
      {fixed > 0 && (
        <Text size="xs" c="dimmed" data-testid={`${testIdPrefix}-applies-own-period`}>
          {COPY.ownPeriodCount(fixed, noun)}
        </Text>
      )}
      {editingThis && (
        <Text size="xs" c="dimmed">
          {say(COPY.picking(plural))}
        </Text>
      )}
    </Stack>
  );
}
